import { rfpAPI } from '../services/api';
import type { VmsApproverPickerContext } from '../constants/vmsAccessMatrix';
import { approverPickerRoleSlugs } from '../constants/vmsAccessMatrix';

export type VmsApproverOption = {
  id: number;
  email?: string;
  first_name?: string;
  last_name?: string;
};

export const KYC_ANNEXURE_CODE = '6';

function displayName(u: VmsApproverOption): string {
  const n = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return n || u.email || `User #${u.id}`;
}

/** Encrypted user dropdown APIs return `{ success, data: User[] }` after decrypt. */
function unwrapEncryptedUserList(decrypted: unknown): VmsApproverOption[] {
  if (Array.isArray(decrypted)) return decrypted as VmsApproverOption[];
  if (decrypted && typeof decrypted === 'object') {
    const data = (decrypted as { data?: unknown }).data;
    if (Array.isArray(data)) return data as VmsApproverOption[];
  }
  return [];
}

async function loadByRole(
  role: string,
  params?: { department_id?: number },
): Promise<VmsApproverOption[]> {
  try {
    if (role === 'business_head') {
      return unwrapEncryptedUserList(await rfpAPI.businessHeadsDropdown(params));
    }
    if (role === 'legal') {
      return unwrapEncryptedUserList(await rfpAPI.vmsLegalTeamDropdown(params));
    }
    if (role === 'dpo') {
      return unwrapEncryptedUserList(await rfpAPI.vmsDpoTeamDropdown(params));
    }
    if (role === 'kyc_team') {
      return unwrapEncryptedUserList(await rfpAPI.vmsKycTeamDropdown());
    }
    if (role === 'admin') {
      return unwrapEncryptedUserList(await rfpAPI.vmsAdminsDropdown(params));
    }
    if (role === 'smf') {
      return unwrapEncryptedUserList(await rfpAPI.vmsSmfTeamDropdown(params));
    }
  } catch {
    return [];
  }
  return [];
}

function approverOptionsCacheKey(
  context: VmsApproverPickerContext,
  params?: { department_id?: number },
): string {
  const dept = params?.department_id ?? '';
  return `${context}|${dept}`;
}

const approverOptionsCache = new Map<string, Promise<VmsApproverOption[]>>();

async function loadVmsApproverOptionsUncached(
  context: VmsApproverPickerContext,
  params?: { department_id?: number },
): Promise<VmsApproverOption[]> {
  const roles = approverPickerRoleSlugs(context);
  const merged = new Map<number, VmsApproverOption>();
  for (const role of roles) {
    const list = await loadByRole(role, params);
    for (const u of list) {
      if (u?.id != null) merged.set(Number(u.id), u);
    }
  }
  return [...merged.values()].sort((a, b) =>
    displayName(a).localeCompare(displayName(b), undefined, { sensitivity: 'base' }),
  );
}

export async function loadVmsDpoApproverOptions(
  params?: { department_id?: number },
): Promise<VmsApproverOption[]> {
  return loadByRole('dpo', params);
}

/** Merge approver dropdown options for Initiator pickers (deduped by id). Cached per context. */
export async function loadVmsApproverOptions(
  context: VmsApproverPickerContext,
  params?: { department_id?: number },
): Promise<VmsApproverOption[]> {
  const key = approverOptionsCacheKey(context, params);
  const cached = approverOptionsCache.get(key);
  if (cached) return cached;

  const pending = loadVmsApproverOptionsUncached(context, params);
  approverOptionsCache.set(key, pending);
  try {
    return await pending;
  } catch (err) {
    approverOptionsCache.delete(key);
    throw err;
  }
}

let kycTeamOptionsPromise: Promise<VmsApproverOption[]> | null = null;

export async function loadVmsKycTeamOptions(): Promise<VmsApproverOption[]> {
  if (!kycTeamOptionsPromise) {
    kycTeamOptionsPromise = loadByRole('kyc_team').catch((err) => {
      kycTeamOptionsPromise = null;
      throw err;
    });
  }
  return kycTeamOptionsPromise;
}

export function approverPickerContextForAnnexureCode(
  code: string,
  fallback: VmsApproverPickerContext,
): VmsApproverPickerContext {
  return String(code).trim() === KYC_ANNEXURE_CODE ? 'kyc_annexure' : fallback;
}

export function splitKycAndGeneralApproverIds(
  allIds: number[],
  kycUserIds: ReadonlySet<number>,
): { kycAssigneeIds: number[]; generalApproverIds: number[] } {
  const kycAssigneeIds: number[] = [];
  const generalApproverIds: number[] = [];
  for (const id of allIds) {
    if (kycUserIds.has(id)) kycAssigneeIds.push(id);
    else generalApproverIds.push(id);
  }
  return { kycAssigneeIds, generalApproverIds };
}

export function mergeKycAndGeneralApproverIds(kycIds: number[], generalIds: number[]): number[] {
  return [
    ...new Set(
      [...generalIds, ...kycIds].filter((n) => Number.isInteger(n) && n > 0),
    ),
  ];
}

export { displayName as vmsApproverDisplayName };
